import { motion, AnimatePresence } from 'framer-motion'
import { X, Drumstick, Droplets, Hand, Heart, Sparkles, TriangleAlert, Zap } from 'lucide-react'
import type { FishDef, FishStats } from '@/data/fish'
import { SegmentedBar } from './SegmentedBar'

interface StatsDrawerProps {
  isOpen: boolean
  fish: FishDef
  stats: FishStats
  water: number
  onFeed: () => void
  onClean: () => void
  onTease: () => void
  onLightShow: () => void
  onClose: () => void
}

export function StatsDrawer({ isOpen, fish, stats, water, onFeed, onClean, onTease, onLightShow, onClose }: StatsDrawerProps) {
  const rows = [
    { icon: Drumstick, label: '饱食度', value: stats.satiety, color: '#f59e0b' },
    { icon: Heart, label: '心情值', value: stats.mood, color: '#f43f5e' },
    { icon: Hand, label: '亲密度', value: stats.bond, color: '#8b5cf6' },
    { icon: Zap, label: '活力值', value: stats.energy, color: '#10b981' },
    { icon: Droplets, label: '水质值', value: water, color: '#0ea5e9' },
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/40 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl rounded-t-[28px] max-h-[75vh] flex flex-col"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl overflow-hidden bg-sky-100">
                  <img src={fish.img} alt={fish.name} className="w-10 h-10 object-contain" draggable={false} />
                </div>
                <div>
                  <h2 className="text-lg font-black tracking-[0.08em] text-[#2f5d8a]">{fish.name}</h2>
                  <p className="text-xs text-slate-400">{fish.species} · {fish.trait}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs font-black tracking-[0.25em] text-slate-400">状态面板</span>
                <span className="flex-1 h-px bg-slate-300/50" />
              </div>

              <div className="flex flex-col gap-3">
                {rows.map(({ icon: Icon, label, value, color }) => (
                  <div key={label} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${color}1a`, color }}>
                      <Icon size={16} />
                    </span>
                    <span className="text-sm font-semibold text-slate-600 w-16 shrink-0">{label}</span>
                    <SegmentedBar value={value} color={color} />
                    <span className="text-sm font-black text-slate-700 w-12 text-right tabular-nums">{value}</span>
                  </div>
                ))}
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-slate-400/10 text-slate-400">
                    <TriangleAlert size={16} />
                  </span>
                  <span className="text-sm font-semibold text-slate-600 w-16 shrink-0">饲养难度</span>
                  <SegmentedBar value={fish.difficulty === '简单' ? 300 : fish.difficulty === '中等' ? 600 : 900} color="#94a3b8" />
                  <span className="text-sm font-black text-slate-500 w-12 text-right">{fish.difficulty}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6 mb-4">
                <span className="text-xs font-black tracking-[0.25em] text-slate-400">快捷操作</span>
                <span className="flex-1 h-px bg-slate-300/50" />
              </div>

              <div className="grid grid-cols-4 gap-3">
                <button
                  onClick={() => { onFeed(); onClose(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                >
                  <Drumstick size={24} className="text-amber-500" />
                  <span className="text-xs font-bold text-amber-700">喂食</span>
                </button>
                <button
                  onClick={() => { onClean(); onClose(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-sky-50 border border-sky-200 hover:bg-sky-100 transition-colors"
                >
                  <Droplets size={24} className="text-sky-500" />
                  <span className="text-xs font-bold text-sky-700">清洁</span>
                </button>
                <button
                  onClick={() => { onTease(); onClose(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-pink-50 border border-pink-200 hover:bg-pink-100 transition-colors"
                >
                  <Hand size={24} className="text-pink-500" />
                  <span className="text-xs font-bold text-pink-700">逗鱼</span>
                </button>
                <button
                  onClick={() => { onLightShow(); onClose(); }}
                  className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
                >
                  <Sparkles size={24} className="text-purple-500" />
                  <span className="text-xs font-bold text-purple-700">光影</span>
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
